import React from 'react'
import { observer } from 'mobx-react-lite'
import DashboardLayout from '../../components/Admin/DashboardLayout'
import { MDBContainer, MDBBtn, MDBIcon, MDBCard, MDBCardBody } from 'mdb-react-ui-kit'
import LoadingSpinner from '../../components/shared/LoadingSpinner.component'
import PageHeading from '../../components/shared/PageHeading.component'
import ProductAvailabilityMap from './components/ProductAvailabilityMap/ProductAvailabilityMap.component'
import Store from './ProductAvailability.store'
import { STATE_NAMES, DISCLAIMER, EMPTY_MESSAGE, formatStateCount } from './ProductAvailability.constants'

import './ProductAvailability.page.scss'

const NONE_COLOR = '#eef0f4',
  LIGHT = [197, 202, 233],
  DARK = [26, 35, 126],
  AVAILABLE_COLOR = `rgb(${DARK.join(',')})`

// 0 products is neutral gray; 1..total runs light indigo → USABG blue.
const shadeFor = (count, total) => {
  if (!count || !total) return NONE_COLOR
  const t = total > 1 ? (count - 1) / (total - 1) : 1
  return `rgb(${LIGHT.map((c, i) => Math.round(c + (DARK[i] - c) * t)).join(',')})`
}

const formatMonthYear = ym => {
  const [year, month] = `${ym || ''}`.split('-').map(n => parseInt(n, 10))
  if (!year || !month) return null
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

const productLabel = product => product.short_name || product.name

// Below desktop the panel sits under the map, so bring it into view after a tap.
const selectState = code => {
  Store.setActiveState(code)
  if (window.matchMedia('(max-width: 1024px)').matches)
    window.requestAnimationFrame(() =>
      document
        .querySelector('#ProductAvailabilityPage .state-panel')
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    )
}

const fillFor = code => {
  const product = Store.selectedProduct
  if (product) return Store.isAvailable(code, product) ? AVAILABLE_COLOR : NONE_COLOR
  return shadeFor(Store.productsByState[code]?.length, Store.total)
}

const labelFor = code => {
  const product = Store.selectedProduct
  if (product)
    return `${STATE_NAMES[code]}: ${product.name} ${Store.isAvailable(code, product) ? 'available' : 'not available'}`
  return `${STATE_NAMES[code]}: ${Store.productsByState[code]?.length || 0} of ${Store.total} products available`
}

const renderLegend = () => {
  if (Store.selectedProduct)
    return (
      <div className='legend' aria-label='Map legend'>
        <span className='legend-product'>{Store.selectedProduct.name}</span>
        <span className='legend-item'>
          <span className='swatch' style={{ background: AVAILABLE_COLOR }} />
          Available
        </span>
        <span className='legend-item'>
          <span className='swatch' style={{ background: NONE_COLOR }} />
          Not available
        </span>
      </div>
    )

  return (
    <div className='legend' aria-label='Map legend'>
      {Array.from({ length: Store.total + 1 }, (_, n) => (
        <span className='legend-item' key={n}>
          <span className='swatch' style={{ background: shadeFor(n, Store.total) }} />
          {n}
        </span>
      ))}
      <span className='legend-caption'>products available</span>
    </div>
  )
}

const renderFilter = () => {
  return (
    <div className='product-filter hide-print'>
      <label htmlFor='product-availability-filter'>Show</label>
      <select
        id='product-availability-filter'
        className='form-select'
        value={Store.view}
        onChange={e => Store.setView(e.target.value)}
      >
        <option value='all'>All products ({Store.total})</option>
        {Store.products.map(product => (
          <option key={product.id} value={`${product.id}`}>
            {product.name}: {formatStateCount(product.states)}
          </option>
        ))}
      </select>
    </div>
  )
}

const renderStatePanel = () => {
  const code = Store.activeState

  if (!code)
    return (
      <aside className='state-panel hide-print' aria-live='polite'>
        <p className='text-muted mb-0'>Hover over, tap, or tab to a state to see its products.</p>
      </aside>
    )

  const available = Store.productsByState[code] || [],
    notes = available.filter(({ note }) => !!note)

  return (
    <aside className='state-panel hide-print' aria-live='polite'>
      <h4 className='state-name'>
        {STATE_NAMES[code]} ({code})
      </h4>
      <div className='state-count'>
        {available.length} of {Store.total} products available
      </div>
      {available.length ? (
        <ul className='state-products'>
          {available.map(({ product }) => (
            <li key={product.id}>{product.name}</li>
          ))}
        </ul>
      ) : (
        <p className='state-none'>No products available.</p>
      )}
      {notes.length ? (
        <div className='state-notes'>
          <strong>Notes</strong>
          <ul>
            {notes.map(({ product, note }) => (
              <li key={product.id}>
                {productLabel(product)}: {note}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </aside>
  )
}

const renderFootnotes = () => {
  const products = Store.products.filter(p => !!p.footnote)
  if (!products.length) return null

  return (
    <section className='footnotes'>
      <h4>Product footnotes</h4>
      {products.map(product => (
        <p key={product.id} className='footnote'>
          <strong>{product.name}:</strong> {product.footnote}
        </p>
      ))}
    </section>
  )
}

const renderContent = () => {
  if (Store.loading && !Store.products.length)
    return <LoadingSpinner isActive={true} page='Product Availability' size='sm' />

  if (Store.error)
    return (
      <MDBCard className='error-state'>
        <MDBCardBody className='text-center py-5'>
          <MDBIcon icon='exclamation-triangle' size='3x' className='text-warning mb-3' />
          <h5>{Store.error}</h5>
          <MDBBtn type='button' color='primary' size='sm' onClick={() => Store.fetch()}>
            Try Again
          </MDBBtn>
        </MDBCardBody>
      </MDBCard>
    )

  const isEmpty = Store.total === 0,
    lastUpdated = formatMonthYear(Store.lastUpdated)

  return (
    <div className='print-area'>
      <div className='page-meta'>
        <h3 className='print-title'>Product Availability by State</h3>
        {lastUpdated ? <div className='last-updated'>Last updated: {lastUpdated}</div> : null}
      </div>

      {isEmpty ? <div className='empty-state'>{EMPTY_MESSAGE}</div> : renderFilter()}

      <div className='map-layout'>
        <div className='map-column'>
          <ProductAvailabilityMap
            fillFor={fillFor}
            labelFor={labelFor}
            activeState={Store.activeState}
            onActivate={Store.setActiveState}
            onSelect={selectState}
          />
          {isEmpty ? null : renderLegend()}
        </div>
        {isEmpty ? null : renderStatePanel()}
      </div>

      {renderFootnotes()}

      <p className='disclaimer'>{DISCLAIMER}</p>
    </div>
  )
}

// Route definitions only accept function/class components, so the observer lives inside.
const ProductAvailability = observer(() => (
  <>
    <MDBContainer fluid className='mt-5 hide-print'>
      <PageHeading
        label='Product Availability by State'
        btnGroup={
          <MDBBtn
            type='button'
            color='indigo'
            className='print-btn'
            onClick={() => window.print()}
            disabled={!!Store.error || (Store.loading && !Store.products.length)}
          >
            <MDBIcon icon='print' />
            &nbsp;Print / Save PDF
          </MDBBtn>
        }
      />
    </MDBContainer>
    <MDBContainer fluid className='mt-2 pb-5'>
      {renderContent()}
    </MDBContainer>
  </>
))

class ProductAvailabilityPage extends React.Component {
  componentDidMount() {
    Store.fetch()
  }

  render() {
    return (
      <DashboardLayout>
        <main id='ProductAvailabilityPage' className='mainSection'>
          <ProductAvailability />
        </main>
      </DashboardLayout>
    )
  }
}

export default ProductAvailabilityPage
